import React, { useState, Suspense, lazy } from 'react';
import { func, shape, bool, string } from 'prop-types';
import { createPortal } from 'react-dom';
import PrivacyTrigger from '@alltrails/shared/components/PrivacyTrigger';
import OutlinedTextField from '../../forms/textFields/OutlinedTextField';
import Toggle from '../../shared/Toggle';
import SelectDropdown from '../../forms/SelectDropdown';
import { descriptionFormPropTypes } from '../../../proptypes/shared';
import { activities } from '../../../utils/activity_util';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import FormButtons from '../FormButtons';
import { form, formField, dateSelect, activitySelect, formToggle, formDateActivity, privacyTrigger } from './styles/styles.module.scss';
import DatePicker from '../../shared/DatePicker';
import { policyToRadioForm, CUSTOM_MAP, VISIBILITY_TO_OPTION } from '../../../utils/privacy_policy_helpers';

const IndividualPrivacyModal = lazy(() => import('../../privacyPolicy/modals/IndividualPrivacyModal'));
const {
  MAP_CREATOR,
  PROFILE: { PRIVACY }
} = StringConstantsMixin.strings;

const DescriptionForm = ({
  initialChangeset,
  changeset,
  updateChangeset,
  handleDescriptionSave,
  isSubmitting,
  closeDescriptionForm,
  locale,
  flexiblePrivacyFeatureFlag
}) => {
  const [showModal, setShowModal] = useState(false);

  const onChange = e => {
    const { name, value } = e.target;
    updateChangeset(prevState => ({ ...prevState, [name]: value }));
  };

  const onDateChange = date => {
    updateChangeset(prevState => ({ ...prevState, date }));
  };

  const onContentPrivacyChange = contentPrivacy => {
    updateChangeset(prevState => ({ ...prevState, contentPrivacy }));
  };

  const onSelect = e => {
    const value = e.currentTarget.dataset.value;
    const updatedActivity = activities.find(activity => activity.value === value);
    updateChangeset(prevState => ({ ...prevState, activity: updatedActivity }));
  };

  const onToggle = () => {
    updateChangeset(prevState => ({ ...prevState, isPrivate: !prevState.isPrivate }));
  };

  const onClear = () => {
    updateChangeset(initialChangeset);
    closeDescriptionForm();
  };

  const saveDescriptionForm = e => {
    closeDescriptionForm();
    handleDescriptionSave(e, true);
  };

  const handlePrivacyClick = e => {
    e.preventDefault();
    setShowModal(true);
  };

  return (
    <>
      <form className={form} onSubmit={saveDescriptionForm}>
        <div>
          <OutlinedTextField
            className={formField}
            name="title"
            changeHandler={onChange}
            value={changeset.title}
            label={MAP_CREATOR.TITLE_LABEL}
            type="text"
          />
        </div>
        <div>
          <OutlinedTextField
            name="description"
            className={formField}
            changeHandler={onChange}
            value={changeset.description || ''}
            label={MAP_CREATOR.DESCRIPTION_LABEL}
            type="text"
            multiline
            rows={3}
          />
        </div>
        <div className={formDateActivity}>
          <SelectDropdown
            id="activity-select"
            ariaLabel={MAP_CREATOR.ACTIVITY}
            nullable
            handleChange={onSelect}
            options={activities}
            defaultItem={changeset.activity}
            className={activitySelect}
            name="activity"
          />
          <DatePicker locale={locale} abbreviateMonth date={changeset.date} className={dateSelect} onChange={onDateChange} disableFuture={false} />
        </div>
        {flexiblePrivacyFeatureFlag ? (
          <PrivacyTrigger
            className={privacyTrigger}
            onClick={handlePrivacyClick}
            label={PRIVACY}
            currentOption={VISIBILITY_TO_OPTION[changeset.contentPrivacy]}
          />
        ) : (
          <Toggle className={formToggle} inputId="mapPrivacyToggle" checked={changeset.isPrivate} onChange={onToggle} label={MAP_CREATOR.PRIVATE} />
        )}

        <FormButtons onCancel={onClear} disabled={isSubmitting} />
      </form>
      {showModal &&
        createPortal(
          <Suspense fallback={null}>
            <IndividualPrivacyModal
              includeModalBackground
              closeModal={() => setShowModal(false)}
              isMounted={showModal}
              acceptCallback={onContentPrivacyChange}
              {...policyToRadioForm(CUSTOM_MAP, changeset.contentPrivacy)}
            />
          </Suspense>,
          document.getElementById('modalPortal')
        )}
    </>
  );
};

DescriptionForm.propTypes = {
  updateChangeset: func.isRequired,
  changeset: shape(descriptionFormPropTypes),
  initialChangeset: shape(descriptionFormPropTypes),
  isSubmitting: bool,
  handleDescriptionSave: func,
  locale: string,
  flexiblePrivacyFeatureFlag: bool
};

DescriptionForm.defaultProps = {
  isSubmitting: false,
  handleDescriptionSave: () => {},
  locale: 'en-US',
  flexiblePrivacyFeatureFlag: false
};

export default DescriptionForm;
